/**
 * Client-generated identifiers.
 *
 * ADR-0001 §4.2 / §6: op ids and entity ids are generated on the device, before any
 * network call, and they are the server's idempotency key. That makes this file a
 * one-way door — the format is referenced by `event_log.op_id`'s unique index and by
 * every object storage key we ever write. Changing it after real evidence exists means
 * a data migration under legal scrutiny.
 *
 * UUIDv7 (RFC 9562 §5.7): 48-bit big-endian Unix millisecond timestamp, then version,
 * then random. Chosen over UUIDv4 because it sorts by creation time, which gives the
 * client useful local ordering (and Postgres a non-random index prefix) without ever
 * making a device clock authoritative.
 *
 * Ordering authority is the server sequence number, always — ADR-0001 §4.5. A UUIDv7
 * embeds a device clock that may be wrong by hours, so it is a tiebreaker for local
 * display, never a source of truth for what happened first.
 */

/** A lowercase, hyphenated RFC 9562 UUID string. */
export type Uuid = string;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/** Max value of the 12-bit `rand_a` field, which we use as a same-millisecond counter. */
const COUNTER_MAX = 0x0fff;

/**
 * Counter seeds are drawn from the low half of the 12-bit space so that a burst of
 * captures inside one millisecond has room to increment without rolling over. RFC 9562
 * §6.2 "monotonic random" method 1.
 */
const COUNTER_SEED_MASK = 0x07ff;

export interface UuidV7Options {
  /** Milliseconds since the Unix epoch. Injected so tests are deterministic. */
  readonly now: () => number;
  /** Fills the given buffer with cryptographically random bytes. */
  readonly randomBytes: (into: Uint8Array) => void;
}

function defaultRandomBytes(into: Uint8Array): void {
  globalThis.crypto.getRandomValues(into);
}

const HEX: readonly string[] = Array.from({ length: 256 }, (_, i) =>
  i.toString(16).padStart(2, '0'),
);

function bytesToUuid(bytes: Uint8Array): Uuid {
  let out = '';
  for (let i = 0; i < 16; i += 1) {
    if (i === 4 || i === 6 || i === 8 || i === 10) out += '-';
    out += HEX[bytes[i] as number];
  }
  return out;
}

/**
 * Creates a monotonic UUIDv7 generator.
 *
 * Monotonicity matters because the outbox drains FIFO per entity (ADR-0001 §4.2). Two
 * ops minted in the same millisecond must still have a stable, distinguishable order,
 * or a create and its immediately-following update can be sent out of sequence.
 *
 * If the injected clock goes backwards — NTP correction, user changing the date, a
 * phone that has been off for a week — we hold the last timestamp rather than emitting
 * a smaller one. A device clock is never allowed to make ids regress.
 */
export function createUuidV7Generator(options?: Partial<UuidV7Options>): () => Uuid {
  const now = options?.now ?? (() => Date.now());
  const randomBytes = options?.randomBytes ?? defaultRandomBytes;

  let lastTimestamp = -1;
  let counter = 0;

  const scratch = new Uint8Array(16);

  return function uuidv7(): Uuid {
    const observed = now();
    let timestamp = Math.max(observed, lastTimestamp);

    if (timestamp === lastTimestamp) {
      counter += 1;
      if (counter > COUNTER_MAX) {
        // Counter exhausted inside one millisecond. Borrow from the next millisecond
        // rather than emitting a duplicate or a non-monotonic id.
        timestamp += 1;
        randomBytes(scratch.subarray(0, 2));
        counter =
          ((((scratch[0] as number) << 8) | (scratch[1] as number)) & COUNTER_SEED_MASK);
      }
    } else {
      randomBytes(scratch.subarray(0, 2));
      counter = ((((scratch[0] as number) << 8) | (scratch[1] as number)) & COUNTER_SEED_MASK);
    }

    lastTimestamp = timestamp;

    const bytes = new Uint8Array(16);
    randomBytes(bytes.subarray(8, 16));

    // 48-bit big-endian timestamp. Number is safe here: 2^48 ms is year 10889.
    bytes[0] = Math.floor(timestamp / 2 ** 40) & 0xff;
    bytes[1] = Math.floor(timestamp / 2 ** 32) & 0xff;
    bytes[2] = Math.floor(timestamp / 2 ** 24) & 0xff;
    bytes[3] = Math.floor(timestamp / 2 ** 16) & 0xff;
    bytes[4] = Math.floor(timestamp / 2 ** 8) & 0xff;
    bytes[5] = timestamp & 0xff;

    // Version 7 in the high nibble, then the 12-bit counter as rand_a.
    bytes[6] = 0x70 | ((counter >>> 8) & 0x0f);
    bytes[7] = counter & 0xff;

    // RFC 9562 variant bits (10xx) in the top of octet 8; the rest stays random.
    bytes[8] = 0x80 | ((bytes[8] as number) & 0x3f);

    return bytesToUuid(bytes);
  };
}

/** Process-wide generator. Injected variants exist for tests. */
export const uuidv7: () => Uuid = createUuidV7Generator();

export function isUuid(value: unknown): value is Uuid {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

/** Extracts the embedded millisecond timestamp. For diagnostics only — never ordering. */
export function uuidV7Timestamp(id: Uuid): number {
  if (!isUuid(id)) throw new TypeError(`not a uuid: ${id}`);
  const hex = id.replace(/-/g, '').slice(0, 12);
  return Number.parseInt(hex, 16);
}
